import { defineComponent as _defineComponent } from 'vue'
import Modal from "@/components/ui/Modal.vue";
  import { reactive, computed, ref, onMounted, watchEffect } from "vue";
  import useVuelidate from "@vuelidate/core";
  import {
    email,
    helpers,
    required,
    not,
    sameAs,
    numeric,
    minLength,
  } from "@vuelidate/validators";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import Cleave from "vue-cleave-component";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import { validPhoneNumber } from "@/helpers/validation";
  import { trackSendInitialHold } from "@/helpers/initialHold/trackSendInitialHold";
  import store from "@/store";
  import { CreateInitialHoldInterface } from "@/types/InitialHoldInterface";
  import { POSITION, useToast } from "vue-toastification";
  import axios, { AxiosError } from "axios";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import { HolderType } from "@/enums/InitialHoldHolderType";
  import { StatesEnum } from "@/enums/StatesEnum";
  import { calculateStateFromPostcode } from "@/helpers/initialHold/calculateStateFromPostcode";
  import { delay } from "@/helpers/async";
  import parseAustralianNumberToInternational from "@/helpers/phone-number";
  import { handleError } from "@/helpers/klaviyo/klaviyo-error-handler.helper";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import { BuilderQuote, UpdateBuilderQuoteDto } from "@/types/BuilderQuote";
  import { CreateBuilderQuote } from "@/types/CreateBuilderQuote";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import { LotMapInterface } from "@/types/LotMapInterface";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { EstateInterface } from "@/types/EstateInterface";
  import { BuilderInterface } from "@/types/BuilderInterface";
  import { LotInterface } from "@/types/LotInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderInitialHoldModal',
  props: {
    isOpen: { type: Boolean },
    lot: {},
    house: {},
    quote: {},
    estate: {},
    builder: {},
    selectedFacade: {}
  },
  emits: ["send", "back", "close", "toPartnerModal"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const userType = ref<string>("");
  const disabled = ref<boolean>(false);
  const toast = useToast();
  const emits = __emit;
  const props = __props;

  const modalContainerRef = ref<HTMLElement | null>(null);
  const contentBlockRef = ref<HTMLElement | null>(null);
  const facade = computed(() => props.selectedFacade);

  const scrollToBottom = async () => {
    await delay(10);

    if (!contentBlockRef.value || !modalContainerRef.value) return;

    contentBlockRef.value.scrollTo({
      top: modalContainerRef.value.scrollHeight,
      behavior: "smooth",
    });
  };

  const customerIdOrNull = computed(() => {
    return store?.state?.user?.data?._id ? store.state.user.data._id : null;
  });

  const formFields = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    holderType: "Please select" as HolderType,
    postcode: "",
    state: "",
  };

  const form = reactive(Object.assign({}, formFields));
  const rules = {
    firstName: { required },
    lastName: { required },
    phone: {
      required,
      validPhoneNumber: helpers.withMessage(
        "Value must be valid phone number",
        validPhoneNumber
      ),
    },
    holderType: {
      required,
      validValue: not(sameAs("Please select")),
    },
    postcode: { required, numeric, minLength: minLength(4) },
    email: { required, email },
  };

  const v$ = useVuelidate(rules, form);

  function onClose() {
    clearForm();
    resetVuelidateErrors(v$);
    emits("close");
  }

  const onReserve = () => {
    if (props.quote.isAdminPackage) {
      createInitialHoldRequest();
    } else {
      updateQuoteAndCreateHold();
    }
  };

  async function createInitialHoldRequest() {
    v$.value.$touch();
    const isValid = await v$.value.$validate();

    if (!isValid) {
      return;
    }

    try {
      disabled.value = true;
      const initialHold = createInitialHoldRequestBody();

      await store.dispatch("initialHold/createInitialHold", initialHold);
      emits("send");
      trackSendInitialHold(props.lot.estateId._id);
      clearForm();
    } catch (error) {
      handleError(error as AxiosError);
    } finally {
      disabled.value = false;
    }
  }

  async function updateQuoteAndCreateHold() {
    v$.value.$touch();
    const isValid = await v$.value.$validate();

    if (!isValid) {
      return;
    }

    try {
      const quote = updatePackageRequest();
      const initialHold = createInitialHoldRequestBody();

      updateQuoteAndInitialHoldRequest(quote, initialHold);
    } catch (error) {
      handleError(error as AxiosError);
    }
  }

  const updateQuoteAndInitialHoldRequest = (
    quote: UpdateBuilderQuoteDto,
    dataInitialHold: CreateInitialHoldInterface
  ) => {
    disabled.value = true;
    store
      .dispatch("builder/requestHoldPackage", {
        quoteId: props.quote._id,
        updateData: quote,
        holdData: dataInitialHold,
      })
      .then(() => {
        emits("send");
        trackSendInitialHold(props.lot.estateId._id);
        clearForm();
      })
      .catch(() => {
        toast.error("Something went wrong, please try again later", {
          position: POSITION.TOP_LEFT,
        });
      })
      .finally(() => {
        disabled.value = false;
      });
  };

  function clearForm() {
    Object.assign(form, formFields);
    resetVuelidateErrors(v$);
  }
  const onBack = () => {
    if (!userType.value) {
      emits("back");
    }

    userType.value = "";
    resetVuelidateErrors(v$);
    clearForm();
  };

  const selectHolderType = (holderType: HolderType) => {
    form.holderType = holderType;
  };

  const updatePackageRequest = () => {
    const { lot } = props;
    const { landownerId } = lot;

    const userSelections = {
      builder: {
        builderId: props.builder._id,
        name: props.builder.name,
        logoUrl: props.builder.logo,
      },
      house: {
        houseId: props.house._id,
        name: props.house.name,
        price: props.house.basePrice,
      },
      facade: {
        facadeId: facade.value?._id as string,
        name: facade.value?.name as string,
        url: facade.value?.url as string,
        price: facade.value?.price as number,
      },
      floorplan: {
        name: "Floorplan",
        url: props.house.floorplans[0].url,
      },
      lot: {
        lotId: lot._id,
        name: lot.title,
        price: lot.price,
      },
    };

    return {
      selections: userSelections,
      timeReference: undefined,
      bestDescribesYou: undefined,
      financeStatus: undefined,
      estateId: props.estate._id,
      estateTitle: props.estate.title,
      landownerId: props.quote.landownerId ?? props.lot.landownerId,
      emailSentToLandowner: true,
      isAdminPackage: false,
      user: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email.toLowerCase(),
        phone: form.phone,
      },
    };
  };

  const createInitialHoldRequestBody = (): CreateInitialHoldInterface => {
    const { firstName, lastName, phone, holderType, postcode } = form;
    const leadDetails = {
      email: form.email.toLowerCase(),
      firstName,
      lastName,
      phone,
    };

    const state = calculateStateFromPostcode(postcode);

    const initialHold: CreateInitialHoldInterface = {
      lotId: props.lot._id,
      lotTitle: props.lot.title,
      landownerId: props.quote.landownerId,
      estateId: props.lot.estateId._id,
      leadDetails,
      holderType,
      state,
      postcode,
      ...(customerIdOrNull.value && { customerId: customerIdOrNull.value }),
    };

    return initialHold;
  };

const __returned__ = { userType, disabled, toast, emits, props, modalContainerRef, contentBlockRef, facade, scrollToBottom, customerIdOrNull, formFields, form, rules, v$, onClose, onReserve, createInitialHoldRequest, updateQuoteAndCreateHold, updateQuoteAndInitialHoldRequest, clearForm, onBack, selectHolderType, updatePackageRequest, createInitialHoldRequestBody, Modal, reactive, computed, ref, onMounted, watchEffect, get useVuelidate() { return useVuelidate }, get email() { return email }, get helpers() { return helpers }, get required() { return required }, get not() { return not }, get sameAs() { return sameAs }, get numeric() { return numeric }, get minLength() { return minLength }, get resetVuelidateErrors() { return resetVuelidateErrors }, get Cleave() { return Cleave }, get phoneNumberOption() { return phoneNumberOption }, get validPhoneNumber() { return validPhoneNumber }, get trackSendInitialHold() { return trackSendInitialHold }, get store() { return store }, get CreateInitialHoldInterface() { return CreateInitialHoldInterface }, get POSITION() { return POSITION }, get useToast() { return useToast }, get axios() { return axios }, get AxiosError() { return AxiosError }, DropDown, get HolderType() { return HolderType }, get StatesEnum() { return StatesEnum }, get calculateStateFromPostcode() { return calculateStateFromPostcode }, get delay() { return delay }, get parseAustralianNumberToInternational() { return parseAustralianNumberToInternational }, get handleError() { return handleError }, get FacadeInterface() { return FacadeInterface }, get BuilderQuote() { return BuilderQuote }, get UpdateBuilderQuoteDto() { return UpdateBuilderQuoteDto }, get CreateBuilderQuote() { return CreateBuilderQuote }, get LeadSourceEnum() { return LeadSourceEnum }, get LotMapInterface() { return LotMapInterface }, get HouseDesignInterface() { return HouseDesignInterface }, get EstateInterface() { return EstateInterface }, get BuilderInterface() { return BuilderInterface }, get LotInterface() { return LotInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})